/**
 * ProjectDetail — Proje detay sayfası (/projeler/:id).
 *
 * Üstte kârlılık/nakit kartları, altta projeye bağlı tüm hareketlerin
 * zaman sıralı akışı (server-side pagination) yer alır.
 *
 * Kritik ayrım: tahsilat/avans yalnız Net Nakit'i etkiler, Kâr'ı DEĞİL.
 * Kâr yalnız faturalaşan tutarlardan (Gelir − Maliyet) doğar.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import CancelIcon from '@mui/icons-material/Cancel';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SavingsIcon from '@mui/icons-material/Savings';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AccountingDataTable from '../shared/AccountingDataTable';
import type { DataTableColumn } from '../shared/AccountingDataTable';
import {
  getProjectById,
  getProjectSummary,
  getProjectTransactions,
  updateProjectStatus,
  type Project,
  type ProjectStatus,
  type ProjectSummary,
  type ProjectFeedItem,
} from '../../../services/projectService';

/**
 * Akış satırından hedef sayfaya yönlendirme.
 * Fatura satış/alış ayrımı etkiden türetilir (Gelir → satış, Maliyet → alış);
 * kayıt vurgusu mümkün olmayan modüllerde ilgili liste sayfasına gidilir.
 */
const resolveDocumentPath = (item: ProjectFeedItem): string | null => {
  switch (item.entityType) {
    case 'Invoice':
      return item.effect === 'COST'
        ? `/fatura/alis/duzenle/${item.entityId}`
        : `/fatura/satis/duzenle/${item.entityId}`;
    case 'Offer':
      return '/teklifler';
    case 'Order':
      return '/siparisler';
    case 'Waybill':
      return '/irsaliyeler';
    case 'CashboxTransaction':
      return '/kasa';
    case 'BankTransaction':
      return '/banka';
    case 'CheckNote':
      return '/cek-senet';
    case 'AccountTransaction':
      return '/cari';
    case 'PersonnelTransaction':
      return '/personnel';
    case 'CreditCardTransaction':
      return '/banka';
    default:
      return null;
  }
};

const EFFECT_COLORS: Record<string, 'success' | 'error' | 'info' | 'default'> = {
  REVENUE: 'success',
  COST: 'error',
  CASH_IN: 'info',
  CASH_OUT: 'info',
  INFO: 'default',
};

const ProjectDetail: React.FC = () => {
  const { t, i18n } = useTranslation('common2');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const localeCode = i18n.language.startsWith('tr') ? 'tr-TR' : 'en-US';
  const projectId = Number(id);

  const [project, setProject] = useState<Project | null>(null);
  const [summary, setSummary] = useState<ProjectSummary | null>(null);
  const [feed, setFeed] = useState<ProjectFeedItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [feedLoading, setFeedLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Durum geçişi (aktif ↔ tamamlandı / iptal)
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [statusTarget, setStatusTarget] = useState<ProjectStatus | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState<string | null>(null);

  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString(localeCode) : '-');

  const fmtMoney = useCallback(
    (value: number) =>
      new Intl.NumberFormat(localeCode, {
        style: 'currency',
        currency: 'TRY',
        maximumFractionDigits: 2,
      }).format(value),
    [localeCode],
  );

  const loadHeader = useCallback(async () => {
    if (!projectId) {
      setError(t('projects.detail.notFound'));
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const [projectData, summaryData] = await Promise.all([
        getProjectById(projectId),
        getProjectSummary(projectId),
      ]);
      setProject(projectData);
      setSummary(summaryData);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, t]);

  const loadFeed = useCallback(async () => {
    if (!projectId) return;
    try {
      setFeedLoading(true);
      const result = await getProjectTransactions(projectId, { page: page + 1, pageSize });
      setFeed(result.data);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setFeedLoading(false);
    }
  }, [projectId, page, pageSize]);

  useEffect(() => {
    loadHeader();
  }, [loadHeader]);

  useEffect(() => {
    loadFeed();
  }, [loadFeed]);

  const refresh = () => {
    loadHeader();
    loadFeed();
  };

  const handleStatusChange = async () => {
    if (!statusTarget || !project) return;
    try {
      setStatusSaving(true);
      const updated = await updateProjectStatus(project.id, statusTarget);
      setProject(updated);
      setStatusTarget(null);
      setSnackbar(t('projects.statusChanged'));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
      setStatusTarget(null);
    } finally {
      setStatusSaving(false);
    }
  };

  /**
   * Excel dışa aktarım: özet kartları + TÜM hareket akışı (sayfa sayfa toplanır;
   * backend pageSize'ı 200 ile sınırlar).
   */
  const exportToExcel = async () => {
    if (!project) return;
    try {
      setExporting(true);

      const rows: ProjectFeedItem[] = [];
      const chunk = 200;
      let current = 1;
      for (;;) {
        const result = await getProjectTransactions(projectId, { page: current, pageSize: chunk });
        rows.push(...result.data);
        if (rows.length >= result.totalCount || result.data.length === 0) break;
        current += 1;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t('projects.title'));

      worksheet.mergeCells('A1:F1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = `${project.name}${project.code ? ` (${project.code})` : ''}`;
      titleCell.style = {
        font: { bold: true, size: 14 },
        alignment: { horizontal: 'center' },
      };

      // Proje tarihleri yalnızca girilmişse yazılır
      if (project.startDate || project.endDate) {
        worksheet.addRow([]);
        if (project.startDate) {
          worksheet.addRow([t('projects.columns.startDate'), fmtDate(project.startDate)]);
        }
        if (project.endDate) {
          worksheet.addRow([t('projects.columns.endDate'), fmtDate(project.endDate)]);
        }
      }

      if (summary) {
        worksheet.addRow([]);
        worksheet.addRow([t('projects.detail.cards.revenue'), summary.revenue]);
        worksheet.addRow([t('projects.detail.cards.cost'), summary.cost]);
        worksheet.addRow([t('projects.detail.cards.profit'), summary.profit]);
        worksheet.addRow([t('projects.detail.feed.cashIn'), summary.cashIn]);
        worksheet.addRow([t('projects.detail.feed.cashOut'), summary.cashOut]);
        worksheet.addRow([t('projects.detail.cards.netCash'), summary.netCash]);
        worksheet.addRow(
          summary.remainingReceivable < 0
            ? [t('projects.detail.cards.excessCollection'), Math.abs(summary.remainingReceivable)]
            : [t('projects.detail.cards.remainingReceivable'), summary.remainingReceivable]
        );
      }

      worksheet.addRow([]);
      const headerRow = worksheet.addRow([
        t('projects.detail.feed.date'),
        t('projects.detail.feed.type'),
        t('projects.detail.feed.effect'),
        t('projects.detail.feed.documentNo'),
        t('projects.detail.feed.account'),
        t('projects.detail.feed.amount'),
      ]);
      headerRow.eachCell((cell) => {
        cell.style = {
          font: { bold: true, color: { argb: 'FFFFFFFF' } },
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4F81BD' } },
          border: {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          },
        };
      });

      rows.forEach((row) => {
        const effects = [
          row.effect && row.effect !== 'INFO' ? row.effect : null,
          row.cashEffect || null,
        ]
          .filter(Boolean)
          .map((badge) => t(`projects.detail.effects.${badge}`, { defaultValue: String(badge) }));

        worksheet.addRow([
          fmtDate(row.date),
          t(`projects.detail.types.${row.entityType}`, { defaultValue: row.entityType }),
          effects.length > 0 ? effects.join(' + ') : t('projects.detail.effects.INFO'),
          row.documentNo || '-',
          [row.accountName, row.description].filter(Boolean).join(' — ') || '-',
          row.amount,
        ]);
      });

      worksheet.columns = [
        { width: 16 },
        { width: 16 },
        { width: 22 },
        { width: 18 },
        { width: 44 },
        { width: 16 },
      ];
      worksheet.getColumn(6).numFmt = '#,##0.00';

      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const safeName = project.name.replace(/[^\p{L}\p{N}]+/gu, '_');
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `Proje_${safeName}_${stamp}.xlsx`);

      setSnackbar(t('projects.detail.exportSuccess'));
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setExporting(false);
    }
  };

  const statusName = (status: ProjectStatus) =>
    status === 'active'
      ? t('projects.statusActive')
      : status === 'completed'
        ? t('projects.statusCompleted')
        : t('projects.statusCancelled');

  const statusChip = (status: string) => {
    const map: Record<string, { color: 'primary' | 'success' | 'default'; label: string }> = {
      active: { color: 'primary', label: t('projects.statusActive') },
      completed: { color: 'success', label: t('projects.statusCompleted') },
      cancelled: { color: 'default', label: t('projects.statusCancelled') },
    };
    const cfg = map[status] || { color: 'default' as const, label: status };
    return <Chip size="small" color={cfg.color} label={cfg.label} />;
  };

  const columns = useMemo<DataTableColumn<ProjectFeedItem>[]>(
    () => [
      {
        id: 'date',
        label: t('projects.detail.feed.date'),
        width: '12%',
        render: (row) => (
          <Typography variant="body2">{fmtDate(row.date)}</Typography>
        ),
      },
      {
        id: 'type',
        label: t('projects.detail.feed.type'),
        width: '12%',
        render: (row) => (
          <Chip
            size="small"
            variant="outlined"
            label={t(`projects.detail.types.${row.entityType}`, { defaultValue: row.entityType })}
          />
        ),
      },
      {
        id: 'effect',
        label: t('projects.detail.feed.effect'),
        width: '16%',
        render: (row) => {
          // Bir hareket iki eksene girebilir (ör. serbest gider: Maliyet + Nakit Çıkış)
          const badges: string[] = [];
          if (row.effect && row.effect !== 'INFO') badges.push(row.effect);
          if (row.cashEffect) badges.push(row.cashEffect);
          if (badges.length === 0) badges.push('INFO');

          return (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {badges.map((badge) => (
                <Chip
                  key={badge}
                  size="small"
                  color={EFFECT_COLORS[badge] || 'default'}
                  label={t(`projects.detail.effects.${badge}`, { defaultValue: badge })}
                />
              ))}
            </Box>
          );
        },
      },
      {
        id: 'documentNo',
        label: t('projects.detail.feed.documentNo'),
        width: '15%',
        render: (row) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {row.documentNo || '-'}
          </Typography>
        ),
      },
      {
        id: 'account',
        label: t('projects.detail.feed.account'),
        minWidth: '220px',
        render: (row) => (
          <Box>
            {row.accountName && (
              <Typography variant="body2" fontWeight={600} noWrap>
                {row.accountName}
              </Typography>
            )}
            {row.description && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {row.description}
              </Typography>
            )}
            {!row.accountName && !row.description && <Typography variant="body2">-</Typography>}
          </Box>
        ),
      },
      {
        id: 'amount',
        label: t('projects.detail.feed.amount'),
        width: '14%',
        align: 'right',
        render: (row) => {
          const isInfoOnly = row.effect === 'INFO' && !row.cashEffect;
          const color =
            row.effect === 'REVENUE'
              ? 'success.main'
              : row.effect === 'COST'
                ? 'error.main'
                : 'text.primary';
          return (
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ color: isInfoOnly ? 'text.disabled' : color }}
            >
              {fmtMoney(row.amount)}
            </Typography>
          );
        },
      },
      {
        id: 'actions',
        label: '',
        width: '60px',
        align: 'center',
        render: (row) => {
          const path = resolveDocumentPath(row);
          if (!path) return null;
          return (
            <Tooltip title={t('projects.detail.feed.goToDocument')}>
              <IconButton size="small" onClick={() => navigate(path)}>
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [t, fmtDate, fmtMoney, navigate],
  );

  const cards = summary
    ? [
        {
          key: 'revenue',
          label: t('projects.detail.cards.revenue'),
          value: fmtMoney(summary.revenue),
          color: 'success.main',
          icon: <TrendingUpIcon />,
          hint: null as string | null,
        },
        {
          key: 'cost',
          label: t('projects.detail.cards.cost'),
          value: fmtMoney(summary.cost),
          color: 'error.main',
          icon: <TrendingDownIcon />,
          hint: null,
        },
        {
          key: 'profit',
          label: t('projects.detail.cards.profit'),
          value: fmtMoney(summary.profit),
          color: summary.profit >= 0 ? 'success.main' : 'error.main',
          icon: <SavingsIcon />,
          hint: null,
        },
        {
          key: 'netCash',
          label: t('projects.detail.cards.netCash'),
          value: fmtMoney(summary.netCash),
          color: summary.netCash >= 0 ? 'info.main' : 'warning.main',
          icon: <AccountBalanceWalletIcon />,
          hint: t('projects.detail.cards.cashDetail', {
            cashIn: fmtMoney(summary.cashIn),
            cashOut: fmtMoney(summary.cashOut),
          }),
        },
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Başlık + geri dön */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/projeler')} aria-label={t('projects.detail.back')}>
          <ArrowBackIcon />
        </IconButton>
        <AccountTreeIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" component="h1" fontWeight="bold">
          {project?.name || t('projects.detail.title')}
        </Typography>
        {project && statusChip(project.status)}
        {/* Başlangıç / Bitiş başlıkla aynı satırda gösterilir */}
        {project && (
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {t('projects.columns.startDate')}: <b>{fmtDate(project.startDate)}</b>
            {' · '}
            {t('projects.columns.endDate')}: <b>{fmtDate(project.endDate)}</b>
          </Typography>
        )}
        {summary && (
          <Chip
            size="small"
            variant="outlined"
            label={t('projects.detail.itemCount', { count: summary.itemCount })}
          />
        )}
        {summary && (
          // Eksi kalan alacak = faturadan fazla tahsilat; kullanıcıya negatif rakam
          // yerine "Fazla Tahsilat (Avans)" olarak gösterilir
          <Tooltip
            title={
              summary.remainingReceivable < 0
                ? t('projects.detail.cards.excessCollectionHint')
                : t('projects.detail.cards.remainingReceivableHint')
            }
          >
            <Chip
              size="small"
              variant="outlined"
              color={summary.remainingReceivable < 0 ? 'info' : 'warning'}
              label={
                summary.remainingReceivable < 0
                  ? `${t('projects.detail.cards.excessCollection')}: ${fmtMoney(Math.abs(summary.remainingReceivable))}`
                  : `${t('projects.detail.cards.remainingReceivable')}: ${fmtMoney(summary.remainingReceivable)}`
              }
            />
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title={t('projects.refresh')}>
          <IconButton onClick={refresh}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t('projects.detail.exportExcel')}>
          <IconButton onClick={exportToExcel} disabled={exporting || !project || totalCount === 0}>
            {exporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
          </IconButton>
        </Tooltip>
        {project && (
          <>
            <Tooltip title={t('projects.statusChangeTitle')}>
              <IconButton onClick={(e) => setStatusMenuAnchor(e.currentTarget)}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={() => setStatusMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {project.status !== 'completed' && (
                <MenuItem onClick={() => { setStatusMenuAnchor(null); setStatusTarget('completed'); }}>
                  <ListItemIcon><DoneAllIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary={t('projects.markCompleted')} />
                </MenuItem>
              )}
              {project.status !== 'cancelled' && (
                <MenuItem onClick={() => { setStatusMenuAnchor(null); setStatusTarget('cancelled'); }}>
                  <ListItemIcon><CancelIcon fontSize="small" color="warning" /></ListItemIcon>
                  <ListItemText primary={t('projects.markCancelled')} />
                </MenuItem>
              )}
              {project.status !== 'active' && (
                <MenuItem onClick={() => { setStatusMenuAnchor(null); setStatusTarget('active'); }}>
                  <ListItemIcon><RestartAltIcon fontSize="small" color="primary" /></ListItemIcon>
                  <ListItemText primary={t('projects.markActive')} />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {project && !loading && project.status !== 'active' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {project.status === 'completed'
            ? t('projects.detail.completedNote')
            : t('projects.detail.cancelledNote')}
        </Alert>
      )}

      {project && !loading && (
        <>
          {/* Temel bilgiler — tarihler başlık satırına taşındı; kod/açıklama yoksa panel hiç görünmez */}
          {(project.code || project.description) && (
            <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {project.code && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('projects.columns.code')}</Typography>
                    <Typography variant="body2" fontWeight="bold">{project.code}</Typography>
                  </Box>
                )}
                {project.description && (
                  <Box sx={{ flexBasis: '100%' }}>
                    <Typography variant="caption" color="text.secondary">{t('projects.modal.description')}</Typography>
                    <Typography variant="body2">{project.description}</Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          )}

          {/* Kârlılık ve nakit kartları */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {cards.map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.key}>
                <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: card.color }}>
                    {card.icon}
                    <Typography variant="caption" color="text.secondary">
                      {card.label}
                    </Typography>
                  </Box>
                  <Typography variant="h6" fontWeight="bold" sx={{ color: card.color }}>
                    {card.value}
                  </Typography>
                  {card.hint && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      {card.hint}
                    </Typography>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Hareket akışı */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            {t('projects.detail.feed.title')}
          </Typography>

          <AccountingDataTable<ProjectFeedItem>
            columns={columns}
            rows={feed}
            getRowId={(row) => `${row.entityType}-${row.entityId}`}
            loading={feedLoading}
            serverSidePagination
            totalCount={totalCount}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage, newPageSize) => {
              setPage(newPage);
              setPageSize(newPageSize);
            }}
            emptyMessage={t('projects.detail.feed.empty')}
            emptyIcon={<AccountTreeIcon sx={{ fontSize: 48, color: 'text.disabled' }} />}
            ariaLabel={t('projects.detail.feed.title')}
            columnResizeStorageKey="accounting-project-feed"
          />

          {!feedLoading && feed.length === 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('projects.detail.feed.emptyHint')}
            </Typography>
          )}
        </>
      )}

      {/* Durum geçişi onayı */}
      <Dialog
        open={Boolean(statusTarget)}
        onClose={statusSaving ? undefined : () => setStatusTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('projects.statusChangeTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('projects.statusChangeText', {
              name: project?.name || '',
              status: statusTarget ? statusName(statusTarget) : '',
            })}
          </DialogContentText>
          <DialogContentText variant="caption" sx={{ mt: 1, display: 'block' }}>
            {t('projects.statusChangeHint')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusTarget(null)} disabled={statusSaving}>
            {t('projects.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleStatusChange}
            disabled={statusSaving}
            startIcon={statusSaving ? <CircularProgress size={16} /> : <DoneAllIcon />}
          >
            {t('projects.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSnackbar(null)}>{snackbar}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectDetail;
